import { NUCLEOTIDE_CODES } from "@/lib/tool-kit/biology-genetics/iupac";

/**
 * Base composition: GC content, GC skew, windowed profiles, codon-position GC and CpG
 * observed/expected. Sequences are upper-case IUPAC letters (U is counted as T).
 *
 * GC content follows the three conventions in Biopython's `gc_fraction`:
 *  - "unambiguous": G, C and S over A, C, G, T, S and W (ambiguous bases left out);
 *  - "length": G, C and S over the whole length, so ambiguity codes count as not-GC;
 *  - "weighted": each ambiguity code adds its own probability of being G or C
 *    (N = 0.5, B and V = 2/3, D and H = 1/3, R, Y, K, M = 0.5).
 */

export type GcMethod = "unambiguous" | "length" | "weighted";

/** Probability that a base drawn at random from the code is G or C. */
function gcWeight(code: string): number {
  const bases = NUCLEOTIDE_CODES[code];
  if (!bases) {
    return 0;
  }
  let gc = 0;
  for (const base of bases) {
    if (base === "G" || base === "C") {
      gc += 1;
    }
  }
  return gc / bases.length;
}

const WEIGHT: Readonly<Record<string, number>> = Object.fromEntries(
  Object.keys(NUCLEOTIDE_CODES).map((code) => [code, gcWeight(code)])
);

/** Codes that are definitely GC (G, C, S) or definitely AT (A, T, U, W). */
const DEFINITE_GC = new Set(["G", "C", "S"]);
const DEFINITE_AT = new Set(["A", "T", "U", "W"]);

export interface Composition {
  length: number;
  a: number;
  c: number;
  g: number;
  /** T and U together. */
  t: number;
  /** Every other IUPAC code (S, W, N, R, …). */
  ambiguous: number;
  /** Letters that are not nucleotide codes (counted in `length`). */
  other: number;
  /** G + C + S. */
  gcCount: number;
  /** A, C, G, T, S and W: the bases whose GC status is certain. */
  definiteCount: number;
  /** Sum of GC probabilities over all letters. */
  weightedGc: number;
  cpg: number;
}

export function countComposition(sequence: string): Composition {
  const out: Composition = {
    length: sequence.length,
    a: 0,
    c: 0,
    g: 0,
    t: 0,
    ambiguous: 0,
    other: 0,
    gcCount: 0,
    definiteCount: 0,
    weightedGc: 0,
    cpg: 0,
  };
  let previous = "";
  for (const base of sequence) {
    if (base === "A") {
      out.a += 1;
    } else if (base === "C") {
      out.c += 1;
    } else if (base === "G") {
      out.g += 1;
    } else if (base === "T" || base === "U") {
      out.t += 1;
    } else if (WEIGHT[base] === undefined) {
      out.other += 1;
    } else {
      out.ambiguous += 1;
    }
    if (DEFINITE_GC.has(base)) {
      out.gcCount += 1;
    }
    if (DEFINITE_GC.has(base) || DEFINITE_AT.has(base)) {
      out.definiteCount += 1;
    }
    out.weightedGc += WEIGHT[base] ?? 0;
    if (previous === "C" && base === "G") {
      out.cpg += 1;
    }
    previous = base;
  }
  return out;
}

/** GC as a fraction 0–1, or null when the denominator is empty. */
export function gcFraction(
  counts: Composition,
  method: GcMethod
): number | null {
  if (method === "unambiguous") {
    return counts.definiteCount > 0
      ? counts.gcCount / counts.definiteCount
      : null;
  }
  if (counts.length === 0) {
    return null;
  }
  return method === "length"
    ? counts.gcCount / counts.length
    : counts.weightedGc / counts.length;
}

/** (G − C) / (G + C), or 0 when there are neither (as Biopython's GC_skew). */
export function gcSkew(g: number, c: number): number {
  return g + c === 0 ? 0 : (g - c) / (g + c);
}

/** CpG observed / expected = CpG × length / (C × G); null when C or G is absent. */
export function cpgObservedExpected(counts: Composition): number | null {
  if (counts.c === 0 || counts.g === 0) {
    return null;
  }
  return (counts.cpg * counts.length) / (counts.c * counts.g);
}

/**
 * GC fraction (unambiguous method) at codon positions 1, 2 and 3 of the frame from base 1.
 * Only complete codons count, as in Biopython's GC123.
 */
export function gcByCodonPosition(
  sequence: string
): [number | null, number | null, number | null] {
  const gc = [0, 0, 0];
  const total = [0, 0, 0];
  const complete = sequence.length - (sequence.length % 3);
  for (let i = 0; i < complete; i++) {
    const base = sequence[i];
    if (DEFINITE_GC.has(base) || DEFINITE_AT.has(base)) {
      total[i % 3] += 1;
      if (DEFINITE_GC.has(base)) {
        gc[i % 3] += 1;
      }
    }
  }
  const frac = (k: number) => (total[k] > 0 ? gc[k] / total[k] : null);
  return [frac(0), frac(1), frac(2)];
}

export interface WindowProfile {
  /** 1-based centre of each window. */
  centres: number[];
  /** GC fraction per window (null where undefined). */
  gc: (number | null)[];
  skew: number[];
  /** The step actually used (raised when the sequence would give too many windows). */
  step: number;
  size: number;
  /** True when the step had to be raised. */
  thinned: boolean;
}

export const MAX_WINDOWS = 2000;

interface Prefixes {
  g: Int32Array;
  c: Int32Array;
  gc: Int32Array;
  definite: Int32Array;
  weight: Float64Array;
}

function buildPrefixes(sequence: string): Prefixes {
  const n = sequence.length;
  const prefixes: Prefixes = {
    g: new Int32Array(n + 1),
    c: new Int32Array(n + 1),
    gc: new Int32Array(n + 1),
    definite: new Int32Array(n + 1),
    weight: new Float64Array(n + 1),
  };
  for (let i = 0; i < n; i++) {
    const base = sequence[i];
    const isGc = DEFINITE_GC.has(base);
    prefixes.g[i + 1] = prefixes.g[i] + (base === "G" ? 1 : 0);
    prefixes.c[i + 1] = prefixes.c[i] + (base === "C" ? 1 : 0);
    prefixes.gc[i + 1] = prefixes.gc[i] + (isGc ? 1 : 0);
    prefixes.definite[i + 1] =
      prefixes.definite[i] + (isGc || DEFINITE_AT.has(base) ? 1 : 0);
    prefixes.weight[i + 1] = prefixes.weight[i] + (WEIGHT[base] ?? 0);
  }
  return prefixes;
}

function windowGc(
  prefixes: Prefixes,
  start: number,
  end: number,
  method: GcMethod
): number | null {
  if (method === "unambiguous") {
    const definite = prefixes.definite[end] - prefixes.definite[start];
    return definite > 0
      ? (prefixes.gc[end] - prefixes.gc[start]) / definite
      : null;
  }
  const span = end - start;
  const total =
    method === "length"
      ? prefixes.gc[end] - prefixes.gc[start]
      : prefixes.weight[end] - prefixes.weight[start];
  return total / span;
}

/** Sliding-window GC and GC skew using prefix sums (O(n) however many windows). */
export function windowProfile(
  sequence: string,
  size: number,
  step: number,
  method: GcMethod
): WindowProfile | null {
  const n = sequence.length;
  if (n === 0) {
    return null;
  }
  const window = Math.min(Math.max(1, Math.floor(size)), n);
  const requested = Math.max(1, Math.floor(step));
  const used = Math.max(requested, Math.ceil((n - window + 1) / MAX_WINDOWS));
  const prefixes = buildPrefixes(sequence);
  const centres: number[] = [];
  const gc: (number | null)[] = [];
  const skew: number[] = [];
  for (let start = 0; start + window <= n; start += used) {
    const end = start + window;
    centres.push(start + (window + 1) / 2);
    skew.push(
      gcSkew(
        prefixes.g[end] - prefixes.g[start],
        prefixes.c[end] - prefixes.c[start]
      )
    );
    gc.push(windowGc(prefixes, start, end, method));
  }
  return {
    centres,
    gc,
    skew,
    step: used,
    size: window,
    thinned: used > requested,
  };
}

/**
 * Cumulative GC skew over consecutive non-overlapping windows: the running sum of
 * (G − C) / (G + C). A bacterial chromosome's minimum and maximum mark the replication
 * origin and terminus (Grigoriev 1998).
 */
export function cumulativeSkew(
  sequence: string,
  size: number
): { positions: number[]; values: number[] } {
  const window = Math.max(1, Math.floor(size));
  const positions: number[] = [];
  const values: number[] = [];
  let running = 0;
  const count = Math.ceil(sequence.length / window);
  const stride = Math.max(1, Math.ceil(count / MAX_WINDOWS));
  let g = 0;
  let c = 0;
  let windows = 0;
  for (let start = 0; start < sequence.length; start += window) {
    g = 0;
    c = 0;
    const end = Math.min(start + window, sequence.length);
    for (let i = start; i < end; i++) {
      if (sequence[i] === "G") {
        g += 1;
      } else if (sequence[i] === "C") {
        c += 1;
      }
    }
    running += gcSkew(g, c);
    windows += 1;
    if (windows % stride === 0 || end === sequence.length) {
      positions.push(end);
      values.push(running);
    }
  }
  return { positions, values };
}
